import { getOcpBaseUrl, getVisArgs } from "./init";
import { Data as OrgStructData, OrgStruct } from "./org-struct";
import { ParamsWatcher, MultiStoreParamsWatcher } from "./params";
import { Store } from "./store";
import { SvgRefStore } from "./svg";
import { VisData, VisDataFetcher } from "./visdata";
import { apiRequest } from "./api";
import { Memoizer } from "./memoizer";

type DataKey = string;
type RefKey = string;

interface OnRefAddCallbackSetup {
    cachedRef: NodeListOf<SVGElement> | undefined;
    callbacks: ((refs: NodeListOf<SVGElement>) => void)[];
}

interface OnDataCallbackSetup {
    lastValue: string | undefined;
    callbacks: (() => void)[];
}

export class VisState<Settings = Record<string, unknown>> {
    private cachedDataSnapshot: Record<DataKey, string> = {};
    private cachedRefsSnapshot: Record<RefKey, NodeListOf<SVGElement>> = {};
    private readonly textContentConnections: Record<RefKey, DataKey> = {};
    private readonly onRefAddCallbacks = new Map<RefKey, OnRefAddCallbackSetup>();
    private readonly onDataCallbacks = new Map<DataKey, OnDataCallbackSetup>();
    public readonly params: ParamsWatcher;
    public onReady?: () => void;

    constructor(
        public readonly data: Store<string>,
        public readonly refs: SvgRefStore,
        public readonly visData: VisDataFetcher,
        public readonly org: OrgStruct,
        paramsWatcher: MultiStoreParamsWatcher,
        public readonly memoizer: Memoizer,
        public readonly settings: Readonly<Settings>,
        public readonly visId: string,
    ) {
        this.data.subscribe(this.update);
        this.refs.subscribe(this.update);

        this.params = new ParamsWatcher(this.visId, this.data, paramsWatcher, this.org);
    }

    public async prepare(): Promise<void> {
        await Promise.all([this.org.loadNets(), this.visData.fetch()]);
        this.onReady?.();
    }

    public get isReady(): boolean {
        return this.org.isReady && this.visData.isReady;
    }

    public connectVisDataText(refKey: RefKey, dataKey: DataKey, title?: string): void {
        if (!this.visData) {
            throw new Error("vis data not initialized");
        }
        this.connectTextContent(refKey, `${this.visData.keyPrefix}${dataKey}`);
        this.onRefAdd(refKey, (refs) => {
            refs.forEach((e) => {
                e.style.cursor = "pointer";
            });
        });
        this.onClick(refKey, () => {
            this.visData?.edit(dataKey, title);
        });
    }

    public connectTextContent(refKey: RefKey, dataKey: DataKey, defaultText?: string): void {
        this.textContentConnections[dataKey] = refKey;
        if (defaultText) {
            this.onRefAdd(refKey, (refs) => {
                refs.forEach((e) => {
                    e.innerHTML = defaultText;
                });
            });
        }
    }

    public connectStyle(
        refKey: RefKey,
        dataKey: DataKey,
        propName: Exclude<keyof CSSStyleDeclaration, "length" | "parentRule">,
        defaultValue: string,
        valueMap: Record<string, string>,
    ): void {
        this.onDataWithRef([dataKey], refKey, (refs) => {
            const value = this.data.get(dataKey)!;
            const mappedValue = valueMap[value] ?? defaultValue;
            refs.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                e.style[propName] = mappedValue as any;
            });
        });
    }

    public connectAttr(
        refKey: RefKey,
        dataKey: DataKey,
        attrName: string,
        defaultValue: string,
        valueMap: Record<string, string>,
    ): void {
        this.onDataWithRef([dataKey], refKey, (refs) => {
            const value = this.data.get(dataKey)!;
            const mappedValue = valueMap[value] ?? defaultValue;
            refs.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                (e as any)[attrName] = mappedValue as any;
            });
        });
    }

    public connectClass(
        refKey: RefKey,
        dataKey: DataKey,
        defaultClass: string,
        valueClassMap: Record<string, string>,
    ): void {
        const allClasses = Array.from(new Set([defaultClass, ...Object.values(valueClassMap)]));
        this.onDataWithRef([dataKey], refKey, (refs) => {
            const value = this.data.get(dataKey)!;
            const mappedValue = valueClassMap[value] ?? defaultClass;
            refs.forEach((e) => {
                e.classList.add(mappedValue);
                e.classList.remove(...allClasses.filter((cls) => cls !== mappedValue));
            });
        });
    }

    public onRefAdd(refKey: RefKey, callback: (refs: NodeListOf<SVGElement>) => void): void {
        const refs = this.refs.get(refKey);
        const setup = this.onRefAddCallbacks.get(refKey) ?? {
            cachedRef: refs,
            callbacks: [],
        };
        setup.callbacks.push(callback);
        this.onRefAddCallbacks.set(refKey, setup);

        // calling the callback right away if the ref already exists
        if (refs) {
            callback(refs);
        }
    }

    public onClick(refKey: RefKey, callback: () => void): void {
        this.onRefAdd(refKey, (refs) => {
            refs.forEach((e) => {
                e.addEventListener("click", callback);
                e.style.cursor = "pointer";
            });
        });
    }

    public link(refKey: RefKey, link: string): void {
        if (link.startsWith("/")) {
            link = `${getOcpBaseUrl()}${link}`;
        }
        this.onClick(refKey, () => {
            window.open(link);
        });
    }

    public onData(keys: DataKey[], callback: () => void): void {
        for (const key of keys) {
            const setup = this.onDataCallbacks.get(key) ?? {
                lastValue: undefined,
                callbacks: [],
            };
            setup.callbacks.push(callback);
            this.onDataCallbacks.set(key, setup);
        }
    }

    public withData<K extends DataKey>(
        keys: K[],
        handler: (data: Record<K, string>) => void,
    ): void {
        this.onData(keys, () => {
            const snap = this.data.getSnapshot();
            for (const key of keys) {
                if (!(key in snap) || snap[key] === undefined) {
                    return;
                }
            }
            handler(snap as Record<K, string>);
        });
    }

    public onDataWithRef(
        keys: DataKey[],
        refKey: RefKey,
        callback: (refs: NodeListOf<SVGElement>) => void,
    ): void {
        const cb = (): void => {
            const refs = this.refs.get(refKey);
            if (!refs) {
                return;
            }
            callback(refs);
        };
        this.onData(keys, cb);
        this.onRefAdd(refKey, cb);
    }

    private update = (): void => {
        const data = this.data.getSnapshot();
        const refs = this.refs.getSnapshot();
        if (Object.is(this.cachedDataSnapshot, data) && Object.is(this.cachedRefsSnapshot, refs)) {
            return;
        }
        this.cachedDataSnapshot = data;
        this.cachedRefsSnapshot = refs;

        // calling all callbacks for changed data
        const called = new Set<() => void>();
        Array.from(this.onDataCallbacks.entries()).forEach(([dataKey, setup]) => {
            const { lastValue, callbacks } = setup;
            if (data[dataKey] === lastValue) {
                return;
            }
            callbacks.forEach((cb) => {
                if (!called.has(cb)) {
                    cb();
                    called.add(cb);
                }
            });
            setup.lastValue = data[dataKey];
        });

        // updating all texts
        Object.entries(this.textContentConnections).forEach(([dataKey, refKey]) => {
            const ref = refs[refKey];
            const val = data[dataKey];
            if (!ref || !val) {
                return;
            }
            ref.forEach((e) => {
                if (e.textContent !== val) {
                    e.textContent = val;
                }
            });
        });

        // calling all callbacks for added refs
        Array.from(this.onRefAddCallbacks.entries()).forEach(([refKey, setup]) => {
            const ref = refs[refKey];
            if (!ref) {
                return;
            }
            const { cachedRef, callbacks } = setup;
            if (cachedRef !== ref) {
                callbacks.forEach((callback) => callback(ref));
                setup.cachedRef = ref;
            }
        });
    };
}

export function prepareVisState<
    Settings extends BasicSettings = BasicSettings,
>(paramsWatcherSettings?: {
    groupByDeviceIfMoreThan?: number;
    maxRealtimeReadingsPerRequest?: number;
    useParallelRealtimeReadings?: boolean;
}): VisState<Settings> {
    const visArgs = getVisArgs<Settings>();
    const state = prepareVisStateFromArgs<Settings>(visArgs);

    if (paramsWatcherSettings?.groupByDeviceIfMoreThan !== undefined) {
        state.params.watcher.groupByDeviceIfMoreThan =
            paramsWatcherSettings?.groupByDeviceIfMoreThan;
    }
    if (paramsWatcherSettings?.maxRealtimeReadingsPerRequest !== undefined) {
        state.params.watcher.maxRealtimeReadingsPerRequest =
            paramsWatcherSettings?.maxRealtimeReadingsPerRequest;
    }
    if (paramsWatcherSettings?.useParallelRealtimeReadings !== undefined) {
        state.params.watcher.useParallelRealtimeReadings =
            paramsWatcherSettings?.useParallelRealtimeReadings;
    }

    state.prepare().catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`State preparation error: ${err}`);
    });
    return state;
}

export async function prepareVissStatesByIds<Settings extends BasicSettings = BasicSettings>(
    orgId: number,
    visIds: number[],
    orgStructData: OrgStructData,
    additionalSettings?: Record<string, unknown>,
    generalParamsWatcher?: MultiStoreParamsWatcher,
): Promise<VisState<Settings>[]> {
    const viss = (await getMultipleVissInfo(orgId, visIds)) ?? [];
    const vissDatas = await getMultipleVissDatas(orgId, visIds);
    const states = viss.map((vis) => {
        const visData = vissDatas[vis.id] || {};
        const state = prepareVisStateFromVisInfo<Settings>(
            vis,
            additionalSettings,
            generalParamsWatcher,
            orgStructData,
            visData,
        );
        return state;
    });

    return states;
}

function prepareVisStateFromVisInfo<Settings extends BasicSettings = BasicSettings>(
    visInfo: VisInfo,
    additionalSettings?: Record<string, unknown>,
    generalParamsWatcher?: MultiStoreParamsWatcher,
    orgStructData?: OrgStructData,
    visData?: VisData,
): VisState<Settings> {
    const source = visInfo.source;

    const regex = /<script.*?>(.*?)<\/script>/s;
    const match = regex.exec(source);
    if (!match || (match && !match[1])) {
        throw new Error(`Can't parse vizualization source: ${visInfo.id}`);
    }

    let settings = JSON.parse(match[1]) as Settings;
    settings = { ...settings, ...{ visName: visInfo.name } };
    if (additionalSettings) {
        settings = { ...settings, ...additionalSettings };
    }

    return prepareVisStateFromArgs({
        orgId: visInfo.orgId,
        netId: visInfo.displayedOnNetId,
        deviceId: visInfo.displayedOnDeviceId,
        visId: visInfo.id,
        settings,
        generalParamsWatcher,
        orgStructData,
        visData,
    });
}

export type BasicSettings = Record<string, unknown> & {
    data?: Record<string, string | null>;
    aliases?: Record<string, string | null>;
};

function prepareVisStateFromArgs<Settings extends BasicSettings = BasicSettings>({
    orgId,
    netId,
    deviceId,
    visId,
    settings,
    generalParamsWatcher,
    orgStructData,
    visData,
}: {
    orgId: number;
    netId?: number;
    deviceId?: number;
    visId: number;
    settings: Settings;
    generalParamsWatcher?: MultiStoreParamsWatcher;
    orgStructData?: OrgStructData;
    visData?: VisData;
}): VisState<Settings> {
    const data = new Store<string>();
    if ("data" in settings && settings.data !== undefined) {
        // const initialData = settings.data;
        const initialData = Object.fromEntries(
            Object.entries(settings.data).filter(([_k, v]) => v !== null),
        ) as Record<string, string>;
        data.setMany(initialData);
    }

    const refs = new Store<NodeListOf<SVGElement>>();

    const visDataFetcher = new VisDataFetcher(data, ":", visId, visData);

    const rawAliases =
        "aliases" in settings && settings.aliases !== undefined ? settings.aliases : {};
    const aliases = Object.fromEntries(
        Object.entries(rawAliases).filter(([_k, v]) => v !== null),
    ) as Record<string, string>;
    const org = new OrgStruct(orgId, aliases, netId, deviceId, orgStructData);

    const memoizer = new Memoizer(data);

    const paramsWatcher = generalParamsWatcher ?? new MultiStoreParamsWatcher();
    // eslint-disable-next-line no-console
    !generalParamsWatcher && paramsWatcher.start().catch(console.error);

    const state = new VisState<Settings>(
        data,
        refs,
        visDataFetcher,
        org,
        paramsWatcher,
        memoizer,
        settings,
        `${visId}`,
    );

    return state;
}

interface VisInfo {
    id: number;
    orgId: number;
    slug: string;
    name: string;
    createdOn: string;
    source: string;
    description?: string;
    displayedOnNetId?: number;
    displayedOnDeviceId?: number;
}

async function getMultipleVissInfo(
    orgId: number,
    visIds: number[],
): Promise<VisInfo[] | undefined> {
    try {
        const res = await apiRequest<VisInfo[]>(`/v1/org/${orgId}/viss/info/`, "post", {
            body: JSON.stringify({
                visIds,
            }),
        });
        return res;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Can't load vizualizations. Error: ${(err as Error).message}`);
        return undefined;
    }
}

type VissDatas = Record<string, Record<string, string>>;

async function getMultipleVissDatas(orgId: number, visIds: number[]): Promise<VissDatas> {
    try {
        const res = await apiRequest<VissDatas>(`/v1/org/${orgId}/viss/data/`, "post", {
            body: JSON.stringify({
                visIds,
            }),
        });
        return res;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Can't load vizualizations data. Error: ${(err as Error).message}`);
        return {};
    }
}
